import type { ReactElement } from 'react';

import { useTranslation } from 'react-i18next';

import { useLocale } from '../../../hooks/useLocale';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import type { ITransactionsData } from '../../../stores/app/types';
import { formatValueWithK } from '../../../utils/helpers';
import type { TransactionStatusTone } from '../../../utils/transactionStatus';
import { getTransactionStatusDisplay, PAYMENT_TRANSACTION_TYPES } from '../../../utils/transactionStatus';
import { BingoBundleRewards } from '../../BingoBundleRewards/BingoBundleRewards';
import { SocialCoin } from '../../SocialCoin/SocialCoin';
import { FundsErrorIcon } from '../../SVGIcons/FundsErrorIcon';
import { InfoIcon } from '../../SVGIcons/InfoIcon';
import { PendingIcon } from '../../SVGIcons/PendingIcon';
import { SuccessIcon } from '../../SVGIcons/SuccessIcon';
import { SweepCoin } from '../../SweepCoin/SweepCoin';
import Tooltip from '../../Tooltip';

import classes from './FundsHistoryDialog.module.scss';

const ICON_BY_TONE: Record<TransactionStatusTone, ReactElement> = {
  pending: <PendingIcon />,
  success: <SuccessIcon />,
  error: <FundsErrorIcon />,
  unknown: <InfoIcon color="yellow" />,
};

type Props = {
  item: ITransactionsData;
  bingoPurchaseSources: string[];
  onTooltipClick: (status: string) => void;
};

const BINGO_BUNDLE_CLASS_NAMES = {
  coinsGroup: classes.coinsGroup,
  coinAmount: classes.coinAmount,
  coinIcon: classes.coinIcon,
  freeDaubIcon: classes.freeDaubIcon,
  freeLabel: classes.freeLabel,
  plus: classes.freeLabel,
  row: classes.freeDaubRow,
};

export function FundsHistoryDialogTransactionRow({ item, bingoPurchaseSources, onTooltipClick }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const BONUS_SUCCESS = { name: t('profile.transactionStatus.success'), icon: <SuccessIcon /> };

  const {
    id,
    transactiontype: transactionType,
    gccoin: gcCoin,
    sccoin: scCoin,
    amount,
    created_at: createdAt,
    moreDetails,
  } = item;

  const statusDisplay = getTransactionStatusDisplay(item.status);
  const status = PAYMENT_TRANSACTION_TYPES.includes(transactionType.toLowerCase())
    ? { name: t(statusDisplay.labelKey), icon: ICON_BY_TONE[statusDisplay.tone] }
    : BONUS_SUCCESS;

  const purchaseSource = moreDetails?.purchaseSource;
  const packageData = moreDetails?.packageData;
  const hasBundleItems = packageData?.bundleItems && packageData.bundleItems.length > 0;

  if (purchaseSource && bingoPurchaseSources.includes(purchaseSource)) {
    if (!hasBundleItems) {
      return null;
    }
  }

  const hasCoinsToDisplay = gcCoin > 0 || scCoin > 0 || (transactionType === 'redeem' && amount > 0);
  const hasSpecialDisplay = ['road', 'snappy gift'].includes(purchaseSource || '');
  const shouldShowStandardCoins = !['bingo_store', 'snappy gift', 'road'].includes(purchaseSource || '');
  if (!hasCoinsToDisplay && !hasBundleItems && !hasSpecialDisplay) {
    return null;
  }

  return (
    <div className={classes.item} data-testid={`funds-history-item-${id}`}>
      <div className={classes.typeSection}>
        <div className={classes.typeDate} data-testid={`funds-history-item-date-${id}`}>
          {formatDate(createdAt)}
        </div>
        <div className={classes.typeName} data-testid={`funds-history-item-type-${id}`}>
          {transactionType.toLowerCase() === 'special bonus'
            ? 'Monthly Bonus'
            : transactionType.toLowerCase().replace('vip', 'VIP')}
        </div>
      </div>

      <div className={classes.coinsSection}>
        {purchaseSource === 'road' && <span className={classes.premiumLabel}>{t('road.premiumLane')}</span>}
        {shouldShowStandardCoins && (
          <>
            {transactionType === 'redeem' ? (
              <span className={classes.cashPrize}>{t('profile.cashPrize')}</span>
            ) : (
              gcCoin > 0 && (
                <div className={classes.coinsGroup}>
                  <SocialCoin className={classes.coinIcon} />
                  <span className={classes.coinAmount} data-testid={`funds-history-item-gc-${id}`}>
                    {formatValueWithK(gcCoin, t)}
                  </span>
                </div>
              )
            )}
          </>
        )}

        {(!!scCoin || (transactionType === 'redeem' && !!amount)) && (
          <>
            {transactionType !== 'redeem' && gcCoin > 0 && (
              <span className={classes.freeLabel}>{t('profile.free')}</span>
            )}
            <div className={classes.coinsGroup}>
              <SweepCoin className={classes.coinIcon} />
              <span className={classes.coinAmount} data-testid={`funds-history-item-sc-${id}`}>
                {formatValueWithK(transactionType === 'redeem' ? Number(amount) : Number(scCoin), t)}
              </span>
            </div>
          </>
        )}
        {hasBundleItems && <BingoBundleRewards classNames={BINGO_BUNDLE_CLASS_NAMES} packageData={packageData} />}
      </div>

      <div className={classes.statusSection} data-testid={`funds-history-item-status-${id}`}>
        {isMobile ? (
          <Tooltip
            supportsNewTooltipDesign
            className={classes.tooltipStatus}
            content={status.name}
            dataTestId={`transaction-status-icon-${id}`}
            handleEvents={() => onTooltipClick(status.name)}
            offset={[0, 5]}
            wrapperClassName={classes.tooltipWrapper}
          >
            {status.icon}
          </Tooltip>
        ) : (
          <>
            {status.icon}
            <span className={classes.statusName}>{status.name}</span>
          </>
        )}
      </div>
    </div>
  );
}
